import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

/**
 * PDF Export Utilities
 *
 * Provides PDF generation with clickable links and intelligent page breaks.
 * Uses html2canvas for rendering and jsPDF for PDF creation.
 *
 * Key Features:
 * - Smart page breaks at table row boundaries (no mid-row splits)
 * - Clickable links preserved from HTML
 * - Fallback to browser print dialog if html2canvas fails
 * - Automatic merging of orphaned content at page bottoms
 */

const PDF_MARGINS = {
  left: 15,
  right: 10,
  top: 10,
  bottom: 20,
};

const PDF_STYLES = {
  container: {
    position: "absolute" as const,
    left: "-9999px",
    top: "0",
    width: "800px",
    backgroundColor: "#ffffff",
    padding: "40px 90px 40px 40px",
    color: "#111",
    boxSizing: "border-box" as const,
  },

  tableStyles: `
    * {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
    }
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
      line-height: 1.5;
    }
    table { 
      page-break-inside: auto;
      width: 100%;
      border-collapse: collapse;
      margin: 0;
    }
    tr { 
      page-break-inside: avoid; 
      page-break-after: auto; 
    }
    thead { 
      display: table-header-group; 
    }
    tfoot { 
      display: table-footer-group; 
    }
    th {
      text-align: left;
      font-weight: 700;
      color: #2b2826;
    }
    td { 
      font-weight: 400; 
      color: #4a4a4a;
      text-align: left;
    }
    img {
      display: block;
      max-width: 100%;
      height: auto;
    }
    div {
      box-sizing: border-box;
    }
  `,

  printStyles: `
    * {
      box-sizing: border-box;
    }
    body { 
      color: #111; 
      padding: 18px;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    }
    table { 
      border-collapse: collapse; 
      width: 100%;
      margin: 0;
    }
    th, td { 
      padding: 8px; 
      text-align: left; 
      border-bottom: 1px solid #ddd; 
    }
    th { 
      background-color: #f5f5f5; 
      font-weight: 700; 
    }
    td { 
      font-weight: 400; 
      color: #4a4a4a; 
    }
    img { 
      max-width: 100%; 
      height: auto;
      display: block;
    }
    h1, h2, h3, h4, h5, h6 { 
      margin-top: 16px; 
      margin-bottom: 8px; 
    }
    @media print {
      body { margin: 0; }
    }
  `,
};

const createPDFContainer = (contentHtml: string): HTMLDivElement => {
  const container = document.createElement("div");
  Object.assign(container.style, PDF_STYLES.container);

  const style = document.createElement("style");
  style.textContent = PDF_STYLES.tableStyles;
  container.appendChild(style);

  const contentWrapper = document.createElement("div");
  contentWrapper.innerHTML = contentHtml;
  contentWrapper.style.width = "100%";
  contentWrapper.style.maxWidth = "100%";
  container.appendChild(contentWrapper);

  return container;
};

const extractLinks = (
  container: HTMLDivElement,
  containerRect: DOMRect
): Array<{
  url: string;
  text: string | null;
  x: number;
  y: number;
  width: number;
  height: number;
}> => {
  const links: Array<{
    url: string;
    text: string | null;
    x: number;
    y: number;
    width: number;
    height: number;
  }> = [];
  const linkElements = container.querySelectorAll("a[href]");

  linkElements.forEach((el) => {
    const link = el as HTMLAnchorElement;
    const rect = link.getBoundingClientRect();
    links.push({
      url: link.href,
      text: link.textContent,
      x: rect.left - containerRect.left,
      y: rect.top - containerRect.top,
      width: rect.width,
      height: rect.height,
    });
  });

  return links;
};

/**
 * Calculate page slices aligned to table row boundaries.
 *
 * Strategy:
 * 1. Try to fit as much content as possible on each page
 * 2. Align slice bottom to last complete table row that fits
 * 3. Merge trailing slices < 8% of page height to avoid orphaned content
 *
 * This prevents awkward mid-row page breaks that split table rows across pages.
 */
const calculateSlices = (
  canvasHeight: number,
  pageHeightPx: number,
  rowRects: Array<{ top: number; bottom: number }>
): Array<{ start: number; height: number }> => {
  const slices: Array<{ start: number; height: number }> = [];
  let y = 0;

  while (y < canvasHeight) {
    const remainingPx = canvasHeight - y;
    let sliceHeightPx = Math.min(pageHeightPx, remainingPx);

    // Find the last table row that fits completely on this page
    const sliceBottomPxCandidate = y + sliceHeightPx;
    let lastRowBottomCandidate = -Infinity;

    for (const rr of rowRects) {
      if (rr.bottom <= sliceBottomPxCandidate && rr.bottom > y) {
        if (rr.bottom > lastRowBottomCandidate) {
          lastRowBottomCandidate = rr.bottom;
        }
      }
    }

    // Align to row boundary if we found one that's not too small
    if (lastRowBottomCandidate !== -Infinity && lastRowBottomCandidate - y > 4) {
      sliceHeightPx = Math.floor(lastRowBottomCandidate - y);
    }

    if (sliceHeightPx < 8) break;

    slices.push({ start: y, height: sliceHeightPx });
    y = Math.min(canvasHeight, y + sliceHeightPx);
  }

  // Merge small trailing slices to avoid orphaned content on final page
  // If last slice is < 8% of page height OR < 8px, merge with previous slice
  if (slices.length > 1) {
    const last = slices[slices.length - 1];
    const prev = slices[slices.length - 2];
    if (last.height < Math.max(8, Math.floor(pageHeightPx * 0.08))) {
      prev.height += last.height;
      slices.pop();
    }
  }

  return slices;
};

/**
 * Add clickable links to PDF page.
 *
 * Expands tiny link areas to minimum 6mm for better usability.
 * Only adds links that fall within the current page slice.
 */
const addLinksToPage = (
  pdf: jsPDF,
  links: Array<{
    url: string;
    text: string | null;
    x: number;
    y: number;
    width: number;
    height: number;
  }>,
  slice: { start: number; height: number },
  imgWidth: number,
  canvasWidth: number,
  pdfHeight: number
) => {
  // Minimum link size ensures clickability even for small text
  const minLinkSizeMm = 6;

  links.forEach((link) => {
    const linkTopPx = link.y;
    if (linkTopPx >= slice.start && linkTopPx < slice.start + slice.height) {
      let linkXmm = PDF_MARGINS.left + (link.x * imgWidth) / canvasWidth;
      let linkYmm = PDF_MARGINS.top + ((linkTopPx - slice.start) * imgWidth) / canvasWidth;
      let linkWmm = (link.width * imgWidth) / canvasWidth;
      let linkHmm = (link.height * imgWidth) / canvasWidth;

      // Expand tiny link hit areas to 6mm minimum for usability
      if (linkWmm < minLinkSizeMm) {
        const expand = (minLinkSizeMm - linkWmm) / 2;
        linkXmm = Math.max(PDF_MARGINS.left, linkXmm - expand);
        linkWmm = minLinkSizeMm;
      }
      if (linkHmm < minLinkSizeMm) {
        const expand = (minLinkSizeMm - linkHmm) / 2;
        linkYmm = Math.max(PDF_MARGINS.top, linkYmm - expand);
        linkHmm = minLinkSizeMm;
      }

      if (linkWmm > 0 && linkHmm > 0 && linkYmm < pdfHeight - PDF_MARGINS.bottom) {
        pdf.link(linkXmm, linkYmm, linkWmm, linkHmm, { url: link.url });
      }
    }
  });
};

const renderPDFPages = (
  pdf: jsPDF,
  canvas: HTMLCanvasElement,
  slices: Array<{ start: number; height: number }>,
  imgWidth: number,
  links: Array<{
    url: string;
    text: string | null;
    x: number;
    y: number;
    width: number;
    height: number;
  }>,
  pdfHeight: number
) => {
  slices.forEach((slice, pageIndex) => {
    const sliceCanvas = document.createElement("canvas");
    sliceCanvas.width = canvas.width;
    sliceCanvas.height = slice.height;

    const ctx = sliceCanvas.getContext("2d")!;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
    ctx.drawImage(
      canvas,
      0,
      slice.start,
      canvas.width,
      slice.height,
      0,
      0,
      canvas.width,
      slice.height
    );

    const sliceData = sliceCanvas.toDataURL("image/png", 1.0);
    const sliceHeightMm = (slice.height * imgWidth) / canvas.width;

    if (pageIndex > 0) pdf.addPage();
    pdf.addImage(
      sliceData,
      "PNG",
      PDF_MARGINS.left,
      PDF_MARGINS.top,
      imgWidth,
      sliceHeightMm,
      undefined,
      "FAST"
    );

    addLinksToPage(pdf, links, slice, imgWidth, canvas.width, pdfHeight);
  });
};

/**
 * Export content as PDF with clickable links and smart page breaks.
 *
 * Process:
 * 1. Create off-screen container with 800px width
 * 2. Extract link positions before rendering
 * 3. Convert to high-quality canvas (2x scale)
 * 4. Slice canvas at table row boundaries
 * 5. Generate multi-page PDF with clickable links
 * 6. Clean up temporary DOM elements
 *
 * Falls back to browser print dialog if html2canvas is unavailable or fails.
 */
export const exportPDF = async (contentHtml: string, filename = "export.pdf") => {
  try {
    if (typeof html2canvas !== "function") {
      console.error("exportPDF: html2canvas is not available");
      exportPDFPrint(contentHtml, filename);
      return;
    }

    const container = createPDFContainer(contentHtml);
    document.body.appendChild(container);

    try {
      // Wait 300ms for fonts to load and CSS to apply before capturing
      // document.fonts.ready doesn't catch all async style loading (e.g. web fonts from CDNs)
      await new Promise((resolve) => setTimeout(resolve, 300));
      await document.fonts.ready;

      const containerRect = container.getBoundingClientRect();
      const links = extractLinks(container, containerRect);

      // Render at 2x scale for better quality
      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        windowWidth: 840,
        windowHeight: container.scrollHeight,
        onclone: (clonedDoc) => {
          const clonedContainer = clonedDoc.querySelector("div > div");
          if (clonedContainer) {
            (clonedContainer as HTMLDivElement).style.width = "800px";
            (clonedContainer as HTMLDivElement).style.maxWidth = "800px";
          }
        },
      });

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pdfWidth - PDF_MARGINS.left - PDF_MARGINS.right;
      const pageHeight = pdfHeight - PDF_MARGINS.top - PDF_MARGINS.bottom;

      const pxPerMm = canvas.width / imgWidth;
      const pageHeightPx = Math.floor(pageHeight * pxPerMm);
      const scaleFactor = canvas.width / containerRect.width;

      // Scale link coordinates to match canvas
      for (let i = 0; i < links.length; i++) {
        links[i].x *= scaleFactor;
        links[i].y *= scaleFactor;
        links[i].width *= scaleFactor;
        links[i].height *= scaleFactor;
      }

      // Get table row boundaries for smart page breaks
      const rowRects = Array.from(container.querySelectorAll("tr")).map((tr) => {
        const r = tr.getBoundingClientRect();
        return {
          top: (r.top - containerRect.top) * scaleFactor,
          bottom: (r.bottom - containerRect.top) * scaleFactor,
        };
      });

      const slices = calculateSlices(canvas.height, pageHeightPx, rowRects);
      renderPDFPages(pdf, canvas, slices, imgWidth, links, pdfHeight);

      pdf.save(filename);
    } finally {
      document.body.removeChild(container);
    }
  } catch (error) {
    console.error("PDF export failed:", error);
    exportPDFPrint(contentHtml, filename);
  }
};

/**
 * Fallback: Export using browser's print dialog.
 *
 * Opens content in new window and triggers print dialog.
 * User can then save as PDF using browser's native print-to-PDF functionality.
 */
export const exportPDFPrint = (contentHtml: string, filename = "export.pdf") => {
  const w = window.open("", "_blank");
  if (!w) {
    console.error("Could not open print window. Please allow popups.");
    return;
  }

  const head = `
    <meta charset="utf-8">
    <title>${filename}</title>
    <style>
      ${PDF_STYLES.printStyles}
    </style>
  `;

  w.document.write(`<!doctype html><html><head>${head}</head><body>${contentHtml}</body></html>`);
  w.document.close();
  w.focus();

  // Delay ensures styles are applied before print dialog opens
  setTimeout(() => {
    w.print();
  }, 250);
};
